import { lineEdgesOnClip } from '@src/editor/services/dubCaptionRows';

import {
  dubLineMatchesFragments,
  lineFragmentRanges,
} from './DubCoverageService';
import { locateDubWord } from './dubWordTimeline';

import type { ClipSpan } from './DubPlacementService';
import type {
  ClipWord,
  DubLineOnClip,
  TimelineSegment,
} from './dubWordTimeline';
import type { TranslationPhrase } from '@src/types.ts/translation';

/**
 * PLAT-11906 — how far the Translation Review's reader has got, by the dub's
 * own clock, while the dubbed voice plays.
 *
 * Pure: no React, no MobX. `buildReviewDubMap` ties each dubbed line to the
 * translation fragments and source words it voices, once per dub and
 * translation; `readReviewDubPlayhead` is the cheap per-tick read.
 *
 * - **Segment level everywhere, word level only where it can be trusted.** A
 *   line reads once its recording has started. On a line whose dub words sit
 *   exactly where the translation's fragments say
 *   (`dubLineMatchesFragments`), the translated text reads word by word
 *   instead: a word reads once its dub word starts. A stale line, or one whose
 *   rebuilt text differs from its snapshot, stays at segment level.
 * - **Nothing here for words outside the dub.** A fragment or source word that
 *   belongs to no line of this dub gets no answer, and the review keeps its
 *   own reading for it.
 */

/** The dub on the review player's clock, as the captions place it. */
export interface DubOnReviewClock {
  timeline: readonly TimelineSegment[];
  placed: ReadonlyMap<string, readonly ClipWord[]>;
  spans: readonly ClipSpan[];
}

/** A translation fragment's identity, the same in the store and the review's
 * rows. */
export function reviewFragmentKey(
  fragment: Pick<TranslationPhrase, 'parentId' | 'originalIndices'>,
): string {
  return `${fragment.parentId ?? ''}|${fragment.originalIndices.join(',')}`;
}

interface ReviewDubLine extends DubLineOnClip {
  /** Where the line's recording starts and ends on the review's clock (the
   * captions' own rule, `lineEdgesOnClip`). */
  startSec: number;
  endSec: number;
  /** Its live fragments, in the order its text joins them, with each one's
   * `[start, end)` in that text. */
  fragments: { key: string; start: number; end: number }[];
  /** True when the dub's words sit where the fragments say. */
  wordLevel: boolean;
}

export interface ReviewDubMap {
  dub: DubOnReviewClock;
  /** The lines this clip shows, by start on the clock. */
  lines: ReviewDubLine[];
  lineById: Map<string, ReviewDubLine>;
  lineOfFragment: Map<string, string>;
  /** Source word (`u:` + its id, or `i:` + its index) → the line voicing it. */
  lineOfSource: Map<string, string>;
}

/** The source element fields a word is found by. */
export interface ReviewSourceWord {
  id?: string;
  source_uuid?: string;
  initial_index: number;
}

export function buildReviewDubMap(
  dub: DubOnReviewClock,
  fragments: readonly TranslationPhrase[],
  elements: Parameters<typeof lineFragmentRanges>[1],
): ReviewDubMap {
  const rebuilt = lineFragmentRanges([...fragments], elements);
  const lines: ReviewDubLine[] = [];
  const lineOfFragment = new Map<string, string>();
  const lineOfSource = new Map<string, string>();
  for (const segment of dub.timeline) {
    const shown = dub.placed.get(segment.segmentId);
    if (!shown || shown.length === 0) continue;
    const now = rebuilt.get(segment.segmentId);
    const firstWord = shown.reduce((first, word) =>
      word.startSec < first.startSec ? word : first,
    );
    const lastWord = shown.reduce((last, word) =>
      word.endSec > last.endSec ? word : last,
    );
    const line: ReviewDubLine = {
      segmentId: segment.segmentId,
      startSec: Math.min(
        lineEdgesOnClip(segment, dub.spans[firstWord.stretchIndex]).startSec,
        firstWord.startSec,
      ),
      endSec: Math.max(
        lineEdgesOnClip(segment, dub.spans[lastWord.stretchIndex]).endSec,
        lastWord.endSec,
      ),
      wordsKnown: segment.wordsKnown,
      fragments: (now?.fragments ?? []).map(range => ({
        key: reviewFragmentKey(range.phrase),
        start: range.start,
        end: range.end,
      })),
      wordLevel: !!now && dubLineMatchesFragments(segment, now),
    };
    lines.push(line);
    for (const range of now?.fragments ?? []) {
      lineOfFragment.set(reviewFragmentKey(range.phrase), line.segmentId);
      for (const id of range.phrase.originalElementIds ?? []) {
        lineOfSource.set(`u:${id}`, line.segmentId);
      }
      for (const index of range.phrase.originalIndices) {
        lineOfSource.set(`i:${index}`, line.segmentId);
      }
    }
  }
  lines.sort((a, b) => a.startSec - b.startSec);
  return {
    dub,
    lines,
    lineById: new Map(lines.map(line => [line.segmentId, line])),
    lineOfFragment,
    lineOfSource,
  };
}

export interface ReviewDubPosition {
  /** The line playing at `t`, else the next one to start — the rule the
   * review already uses for its active phrase. */
  activeLineId: string | null;
  /** The fragment the reader is at: on a word-level line, the one holding the
   * word just reached; otherwise the active line's first. */
  activeFragmentKey: string | null;
  /**
   * How much of a fragment has been read: `true`, `false`, or — for the
   * fragment the frontier is inside — how many characters of its trimmed text
   * have been reached. `undefined` when the fragment voices no line here.
   */
  fragmentRead(key: string): boolean | number | undefined;
  /** Has the line voicing this source word started? `undefined` when the
   * word belongs to no line here. */
  sourceRead(word: ReviewSourceWord): boolean | undefined;
  /** The same for a phrase of another language, by the source words it
   * covers. */
  phraseRead(
    phrase: Pick<TranslationPhrase, 'originalElementIds' | 'originalIndices'>,
  ): boolean | undefined;
}

export function readReviewDubPlayhead(
  map: ReviewDubMap,
  t: number,
): ReviewDubPosition {
  const located = locateDubWord(map.dub.placed, map.lines, t);
  const activeLine = located
    ? map.lineById.get(located.segmentId)
    : map.lines.find(line => line.startSec > t);
  // On a word-level line, how far into its text the reader has got: the end
  // of the word last started, or nothing yet in the silence before its first.
  const frontier =
    located && activeLine?.wordLevel
      ? {
          segmentId: located.segmentId,
          charStart: 'charStart' in located ? located.charStart : 0,
          charEnd: 'charEnd' in located ? located.charEnd : 0,
        }
      : null;

  const activeFragmentKey = !activeLine
    ? null
    : ((frontier &&
        activeLine.fragments.find(
          range =>
            range.start <= frontier.charStart && frontier.charStart < range.end,
        )?.key) ??
      activeLine.fragments[0]?.key ??
      null);

  const lineStarted = (lineId: string | undefined): boolean | undefined => {
    const line = lineId ? map.lineById.get(lineId) : undefined;
    return line ? t >= line.startSec : undefined;
  };

  return {
    activeLineId: activeLine?.segmentId ?? null,
    activeFragmentKey,
    fragmentRead(key) {
      const line = map.lineById.get(map.lineOfFragment.get(key) ?? '');
      if (!line) return undefined;
      if (t < line.startSec) return false;
      if (!frontier || frontier.segmentId !== line.segmentId) return true;
      const range = line.fragments.find(candidate => candidate.key === key);
      if (!range) return true;
      const reached = frontier.charEnd - range.start;
      if (reached <= 0) return false;
      if (reached >= range.end - range.start) return true;
      return reached;
    },
    sourceRead(word) {
      for (const id of [word.source_uuid, word.id]) {
        if (id && map.lineOfSource.has(`u:${id}`)) {
          return lineStarted(map.lineOfSource.get(`u:${id}`));
        }
      }
      return lineStarted(map.lineOfSource.get(`i:${word.initial_index}`));
    },
    phraseRead(phrase) {
      for (const id of phrase.originalElementIds ?? []) {
        if (map.lineOfSource.has(`u:${id}`)) {
          return lineStarted(map.lineOfSource.get(`u:${id}`));
        }
      }
      for (const index of phrase.originalIndices) {
        if (map.lineOfSource.has(`i:${index}`)) {
          return lineStarted(map.lineOfSource.get(`i:${index}`));
        }
      }
      return undefined;
    },
  };
}
